// Simple fetch wrapper that sets X-Tenant header from current path when available and Authorization header from stored token.
// If REACT_APP_API_URL is provided at build time it will be used as the API base.
// Otherwise, an empty string is used so requests go to the same origin that serves the frontend
// (this is the recommended setup when the frontend is served from the API's wwwroot).
let API_BASE = process.env.REACT_APP_API_URL || '';
// In development, if API_BASE is empty and we're on localhost, use the backend
if (!API_BASE && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    API_BASE = 'https://localhost:7239';
}

function getTenantSlugFromPath() {
    try {
        const path = window.location.pathname || "";
        const segments = path.replace(/^\/+|\/+$/g, "").split("/");
        // support /t/{slug}
        if (segments.length >= 2 && segments[0].toLowerCase() === "t") {
            return segments[1];
        }
        
        // support leading single-segment tenant (e.g. /nonnystores/...) but avoid common non-tenant top-level routes
        const top = segments[0]?.toLowerCase();
        const nonTenantPrefixes = ["api", "login", "register", "forgot-password", "verify-email", "reset-password", "static", "assets", "favicon.ico", "superadmin", "app"];
        if (segments.length >= 1 && top && !nonTenantPrefixes.includes(top)) {
            return segments[0];
        }
    } catch (err) {
        // ignore
    }
    return null;
}

function getAuthToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || null;
    } catch (err) {
        return null;
    }
}

function getApiKey() {
    try {
        return process.env.REACT_APP_API_KEY || localStorage.getItem("apiKey") || null;
    } catch (err) {
        return null;
    }
}

function parseJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return json;
    } catch (e) {
        return null;
    }
}

export async function apiFetch(path, options = {}) {
    // Allow callers to opt-out of tenant header (useful for self-provisioning / register)
    const { skipTenant, ...fetchOptions } = options || {};

    // Determine tenant: prefer stored tenant in localStorage (set on login), fall back to path (/t/{slug})
    let tenant = null;
    if (!skipTenant) {
        tenant = localStorage.getItem("tenant") || null;
        if (!tenant) {
            tenant = getTenantSlugFromPath();
        }
    }

    const token = getAuthToken();
    const apiKey = getApiKey();

    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...((options && options.headers) || {}),
    };

    if (tenant) {
        headers["X-Tenant"] = tenant;
    }

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    if (apiKey) {
        headers["X-Api-Key"] = apiKey;
    }

    const fullUrl = `${API_BASE}${path}`;
    console.log('API Request:', { API_BASE, path, fullUrl });
    const res = await fetch(fullUrl, { ...fetchOptions, headers, credentials: fetchOptions.credentials ?? 'include' });

    // Try to parse JSON body so callers receive consistent parsed body
    let parsed = null;
    try {
        parsed = await res.clone().json();
    } catch (err) {
        // not JSON or empty
    }

    // If unauthorized, log diagnostic info to help debugging (does not print token value)
    if (res.status === 401) {
        try {
            const authPresent = Boolean(headers['Authorization']);
            let tokenExpired = null;
            try {
                if (authPresent && token) {
                    const payload = parseJwtPayload(token);
                    if (payload && payload.exp) {
                        const now = Math.floor(Date.now() / 1000);
                        tokenExpired = payload.exp <= now;
                    }
                }
            } catch (e) {
                // ignore
            }
            console.warn(`apiFetch 401 for ${path} � Authorization header present: ${authPresent}, tokenExpired: ${tokenExpired}, X-Tenant: ${headers['X-Tenant'] ? 'yes' : 'no'}, X-Api-Key: ${headers['X-Api-Key'] ? 'yes' : 'no'}`);
            if (parsed && typeof parsed === 'object') {
                // avoid logging token values
                const clone = { ...parsed };
                if (clone.token) delete clone.token;
                if (clone.Token) delete clone.Token;
                console.warn(`apiFetch 401 body for ${path}:`, clone);
            } else if (parsed) {
                console.warn(`apiFetch 401 body for ${path}:`, parsed);
            }
            // Dispatch a global unauthorized event so the app can react (logout/redirect)
            try {
                // only dispatch if an auth token was present to avoid noisy events for anonymous requests
                if (token) {
                    const ev = new CustomEvent('unauthorized', { detail: { path, tokenPresent: true } });
                    window.dispatchEvent(ev);
                }
            } catch (e) {
                // ignore
            }
        } catch (e) {
            // ignore logging errors
        }
    }

    // Return a lightweight wrapper with same surface used by callers (.ok, .status, json())
    return {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
        async json() {
            // Return full parsed body (do NOT unwrap .data automatically) so callers see original response shape
            if (parsed !== null) return parsed;
            try {
                return await res.json();
            } catch (e) {
                return null;
            }
        },
        async text() {
            try {
                return await res.text();
            } catch (e) {
                return '';
            }
        }
    };
}

// Superadmin helpers
export async function getTenants(page = 1, pageSize = 50) {
    const res = await apiFetch(`/api/superadmin/tenants?page=${page}&pageSize=${pageSize}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function getTenant(id) {
    const res = await apiFetch(`/api/superadmin/tenants/${id}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function createTenant(dto) {
    const res = await apiFetch(`/api/superadmin/tenants`, { method: 'POST', skipTenant: true, body: JSON.stringify(dto) });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function updateTenant(id, dto) {
    const res = await apiFetch(`/api/superadmin/tenants/${id}`, { method: 'PUT', skipTenant: true, body: JSON.stringify(dto) });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function deleteTenant(id) {
    const res = await apiFetch(`/api/superadmin/tenants/${id}`, { method: 'DELETE', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body;
}

export async function getTenantSales(tenantId, page = 1, pageSize = 50) {
    const res = await apiFetch(`/api/superadmin/tenants/${tenantId}/sales?page=${page}&pageSize=${pageSize}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function getTenantProducts(tenantId, page = 1, pageSize = 50) {
    const res = await apiFetch(`/api/superadmin/tenants/${tenantId}/products?page=${page}&pageSize=${pageSize}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function getTenantVendors(tenantId, page = 1, pageSize = 50) {
    const res = await apiFetch(`/api/superadmin/tenants/${tenantId}/vendors?page=${page}&pageSize=${pageSize}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}

export async function getTenantInventory(tenantId, page = 1, pageSize = 50) {
    const res = await apiFetch(`/api/superadmin/tenants/${tenantId}/inventory?page=${page}&pageSize=${pageSize}`, { method: 'GET', skipTenant: true });
    if (!res.ok) throw await res.json().catch(() => ({ message: res.statusText }));
    const body = await res.json();
    return body.data || body;
}
